/**
 * SmsConsentCard — #1212 texting B2a. Whether this client said yes to texts,
 * when and how, on the customer profile.
 *
 * The Counter Test it answers: a parent at the desk says "stop texting me",
 * or the owner needs to show a carrier exactly when a client agreed. One
 * glance shows Yes / No / No answer for booking texts and offers, with the
 * date and how; one tap records "they asked us to stop".
 *
 * The rule is the server's (decision of record 2026-09-21, #682): staff can
 * record a NO, never a YES. This card has no control that grants, and says
 * so; the client says yes in their own app.
 *
 * DESIGN-PHILOSOPHY:
 *  · P1 — each kind is one line in words (Yes since…, Stopped…, No answer).
 *  · P2 — one action per kind, shown only when it does something (a Yes on file).
 *  · P5 — a failed load is an error with Retry, never "No answer" (which would
 *    read as "this client never agreed").
 *  · Five states: loading (skeleton), error (+Retry), empty (no answer on
 *    file, with the teaching line), ready, saving (button busy).
 *  · Tokens only (reuses the #682 card's classes).
 */
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Skeleton } from '@ferro/ui';
import { humanizeApiError } from '../../lib/api.js';
import {
  describeSource,
  fetchCustomerSmsConsent,
  recordCustomerSmsOptOut,
  type SmsConsentKind,
  type SmsConsentStatus,
  type StaffSmsConsentView,
} from '../../lib/sms-consent-api.js';

export interface SmsConsentCardProps {
  readonly apiUrl: string;
  readonly customerId: string;
  /** customers:write:all — may record an opt-out. UX gate only; the server decides. */
  readonly canWrite?: boolean;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; view: StaffSmsConsentView };

const KINDS: ReadonlyArray<{ kind: SmsConsentKind; label: string }> = [
  { kind: 'transactional', label: 'Booking texts' },
  { kind: 'marketing', label: 'Offer texts' },
];

function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'on an unknown date';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function statusLine(s: SmsConsentStatus): string {
  switch (s.status) {
    case 'none':
      return 'No answer on file';
    case 'granted':
      return `Yes since ${formatDay(s.at)}, ${describeSource(s.source)}`;
    case 'revoked':
      return `Stopped ${formatDay(s.at)}, ${describeSource(s.source)}`;
    default: {
      const unhandled: never = s;
      return unhandled;
    }
  }
}

export function SmsConsentCard({ apiUrl, customerId, canWrite = false }: SmsConsentCardProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [saving, setSaving] = useState<SmsConsentKind | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const res = await fetchCustomerSmsConsent(apiUrl, customerId);
    if (res.error || !res.data) {
      setState({ kind: 'error', message: humanizeApiError(res.error ?? null) });
      return;
    }
    setState({ kind: 'ready', view: res.data });
  }, [apiUrl, customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function optOut(kind: SmsConsentKind): Promise<void> {
    setSaving(kind);
    setSaveError(null);
    const res = await recordCustomerSmsOptOut(apiUrl, customerId, kind);
    setSaving(null);
    if (res.error || !res.data) {
      setSaveError(humanizeApiError(res.error ?? null));
      return;
    }
    setState({ kind: 'ready', view: res.data });
  }

  return (
    <article className="ferro-dashboard__card" aria-label="Text messages" data-testid="sms-consent-card">
      <header className="ferro-dashboard__cardHeader">
        <h2 className="ferro-dashboard__cardTitle">Text messages</h2>
      </header>

      {state.kind === 'loading' ? (
        <div aria-busy="true" data-testid="sms-consent-card-loading">
          <Skeleton variant="text" lines={3} />
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div data-testid="sms-consent-card-error">
          <Alert variant="error">We couldn&apos;t load this client&apos;s texting consent. {state.message}</Alert>
          <div className="ferro-settings__actions">
            <Button type="button" variant="secondary" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          <p className="ferro-muted ferro-notif-prefs__provenance">
            Only the client can say yes to texts, in their app. You can record that they asked to stop.
            {state.view.phone_last4 ? ` Number on file ends in ${state.view.phone_last4}.` : ' No textable number on file.'}
          </p>
          <dl className="ferro-settings__readonly">
            {KINDS.map(({ kind, label }) => {
              const s = state.view[kind];
              return (
                <div key={kind} className="ferro-settings__readonly-row">
                  <dt>{label}</dt>
                  <dd data-testid={`sms-consent-status-${kind}`} data-status={s.status}>
                    <span
                      className={`ferro-profile-chip ferro-profile-chip--${s.status === 'granted' ? 'success' : 'neutral'}`}
                    >
                      {s.status === 'granted' ? 'Yes' : 'No'}
                    </span>{' '}
                    <span className="ferro-muted">{statusLine(s)}</span>
                    {canWrite && s.status === 'granted' ? (
                      <div className="ferro-settings__actions">
                        <Button
                          type="button"
                          variant="secondary"
                          isLoading={saving === kind}
                          disabled={saving !== null}
                          onClick={() => void optOut(kind)}
                          data-testid={`sms-consent-optout-${kind}`}
                        >
                          They asked us to stop
                        </Button>
                      </div>
                    ) : null}
                  </dd>
                </div>
              );
            })}
          </dl>
          {saveError ? (
            <div data-testid="sms-consent-card-save-error">
              <Alert variant="error">{saveError}</Alert>
            </div>
          ) : null}
          {state.view.history.length > 0 ? (
            <ul className="ferro-muted" data-testid="sms-consent-card-history">
              {state.view.history.map((h) => (
                <li key={h.id}>
                  {formatDay(h.at)}: {h.action === 'granted' ? 'said yes to' : 'stopped'}{' '}
                  {h.kind === 'transactional' ? 'booking texts' : 'offer texts'} {describeSource(h.source)} (number
                  ending in {h.phone_last4})
                </li>
              ))}
            </ul>
          ) : (
            <p className="ferro-muted" data-testid="sms-consent-card-empty">
              Nothing on file yet. The client is asked when they sign up, and once when they next sign in.
            </p>
          )}
        </>
      ) : null}
    </article>
  );
}
